import { signal, computed } from '@preact/signals';
import type { AppState, Recipe, Week, ShoppingItem, Settings, MealType } from '../types';
import { fetchState, putState, ConflictError, isOffline } from './api';
import { saveLocal, loadLocal } from './localCache';
import { buildInitialState } from '../lib/seedImport';
import { newId } from '../lib/ids';
import { addDays, daysSince } from '../lib/dates';

export const state = signal<AppState | null>(null);
export const slug = signal<string>('');
export const conflictPending = signal<AppState | null>(null);
export const loading = signal<boolean>(false);
export const lastError = signal<string | null>(null);

// Backfills any fields added after a recipe was first written, so older KV
// records keep working when the schema grows.
function migrateState(s: AppState): AppState {
  return {
    ...s,
    recipes: s.recipes.map(r => ({
      ...r,
      category: (r.category ?? null) as Recipe['category'],
      cuisine: (r.cuisine ?? null) as Recipe['cuisine'],
      cook_minutes: r.cook_minutes ?? null,
      servings: r.servings ?? null,
      instructions: r.instructions ?? [],
      archived: r.archived ?? false,
      source_name: r.source_name ?? null,
      image_focus: r.image_focus ?? null,
    })),
  };
}

export const migrateStateForTest = migrateState;

export async function bootstrap(familySlug: string, nowIso = new Date().toISOString()): Promise<void> {
  slug.value = familySlug;
  loading.value = true;
  try {
    let server = await fetchState(familySlug);
    if (!server) {
      const initial = buildInitialState(familySlug, nowIso);
      server = await putState(familySlug, initial, null);
    }
    const migrated = migrateState(server);
    state.value = migrated;
    saveLocal(familySlug, migrated);
  } catch (e) {
    const cached = loadLocal(familySlug);
    if (cached) state.value = migrateState(cached);
    else lastError.value = (e as Error).message;
  } finally {
    loading.value = false;
  }
}

async function persist(next: AppState, ifMatch: string | null): Promise<void> {
  state.value = next;
  saveLocal(slug.value, next);
  try {
    const after = await putState(slug.value, next, ifMatch);
    state.value = after;
    saveLocal(slug.value, after);
  } catch (e) {
    if (e instanceof ConflictError) {
      conflictPending.value = e.serverState;
    } else {
      lastError.value = (e as Error).message;
    }
  }
}

function withTimestamp(next: AppState): AppState {
  return { ...next, updated_at: new Date().toISOString() };
}

let mutateChain: Promise<void> = Promise.resolve();

export async function mutate(fn: (s: AppState) => AppState): Promise<void> {
  const run = mutateChain.then(async () => {
    const current = state.value;
    if (!current) return;
    // If-Match must be the timestamp we last read, not the one we're about to write.
    await persist(withTimestamp(fn(current)), current.updated_at);
  });
  mutateChain = run.catch(() => {}); // keep chain alive on error
  await run;
}

export async function resolveConflictKeepServer(): Promise<void> {
  const server = conflictPending.value;
  if (!server) return;
  state.value = server;
  saveLocal(slug.value, server);
  conflictPending.value = null;
}

// Convenience selectors as computed signals.
export const recipes = computed(() => state.value?.recipes ?? []);
export const weeks = computed(() => state.value?.weeks ?? []);
export const settings = computed(() => state.value?.settings);

// Helper mutators used by pages
export async function upsertRecipe(r: Recipe): Promise<void> {
  await mutate(s => {
    const idx = s.recipes.findIndex(x => x.id === r.id);
    const recipes = [...s.recipes];
    if (idx === -1) recipes.push(r);
    else recipes[idx] = r;
    return { ...s, recipes };
  });
}

export async function archiveRecipe(id: string): Promise<void> {
  await mutate(s => ({
    ...s,
    recipes: s.recipes.map(r => r.id === id ? { ...r, archived: true } : r),
  }));
}

export async function restoreRecipe(id: string): Promise<void> {
  await mutate(s => ({
    ...s,
    recipes: s.recipes.map(r => r.id === id ? { ...r, archived: false } : r),
  }));
}

export async function setMealSlot(
  weekId: string,
  date: string,
  mealType: MealType,
  recipeId: string | null,
): Promise<void> {
  await mutate(prev => {
    const existing = prev.weeks.find(w => w.id === weekId);
    const w: Week = existing ?? {
      id: weekId, start_date: weekId, meals: [],
      shopping_list: [], shopping_list_generated_at: null, exported_at: null,
    };
    const meals = w.meals.filter(m => !(m.date === date && m.meal_type === mealType));
    if (recipeId) {
      meals.push({
        date, meal_type: mealType, recipe_id: recipeId,
        rating: null, rated_at: null, note: '',
      });
    }
    const updated: Week = { ...w, meals };
    const weeks = prev.weeks.some(x => x.id === weekId)
      ? prev.weeks.map(x => x.id === weekId ? updated : x)
      : [...prev.weeks, updated];
    return { ...prev, weeks };
  });
}

// Append a meal to a (date, meal_type) slot without removing existing entries.
// Dedupes the same recipe in the same slot — if the recipe is already there,
// this is a no-op so a re-add doesn't double up.
export async function addMealToSlot(
  weekId: string,
  date: string,
  mealType: MealType,
  recipeId: string,
): Promise<void> {
  await mutate(prev => {
    const existing = prev.weeks.find(w => w.id === weekId);
    const w: Week = existing ?? {
      id: weekId, start_date: weekId, meals: [],
      shopping_list: [], shopping_list_generated_at: null, exported_at: null,
    };
    const already = w.meals.some(m =>
      m.date === date && m.meal_type === mealType && m.recipe_id === recipeId
    );
    if (already) return prev;
    const meals = [...w.meals, {
      date, meal_type: mealType, recipe_id: recipeId,
      rating: null, rated_at: null, note: '',
    }];
    const updated: Week = { ...w, meals };
    const weeks = prev.weeks.some(x => x.id === weekId)
      ? prev.weeks.map(x => x.id === weekId ? updated : x)
      : [...prev.weeks, updated];
    return { ...prev, weeks };
  });
}

// Remove the single WeekMeal whose (date, meal_type, recipe_id) all match,
// leaving any other meals in the slot intact.
export async function removeMealFromSlot(
  weekId: string,
  date: string,
  mealType: MealType,
  recipeId: string,
): Promise<void> {
  await mutate(prev => {
    const existing = prev.weeks.find(w => w.id === weekId);
    if (!existing) return prev;
    const meals = existing.meals.filter(m =>
      !(m.date === date && m.meal_type === mealType && m.recipe_id === recipeId)
    );
    if (meals.length === existing.meals.length) return prev;
    const updated: Week = { ...existing, meals };
    return { ...prev, weeks: prev.weeks.map(x => x.id === weekId ? updated : x) };
  });
}

// Copy meals from a source week into a target week, aligning by day-of-week
// offset (source Monday → target Monday, etc). Skips target slots that already
// have any meal — does not overwrite. Single atomic mutation.
export async function copyWeek(
  sourceWeekId: string,
  targetWeekId: string,
): Promise<{ copied: number; skipped: number }> {
  let copied = 0;
  let skipped = 0;
  await mutate(prev => {
    const source = prev.weeks.find(w => w.id === sourceWeekId);
    if (!source) return prev;
    const targetExisting = prev.weeks.find(w => w.id === targetWeekId);
    const target: Week = targetExisting ?? {
      id: targetWeekId, start_date: targetWeekId, meals: [],
      shopping_list: [], shopping_list_generated_at: null, exported_at: null,
    };
    const newMeals = [...target.meals];
    for (const sm of source.meals) {
      if (!sm.recipe_id) continue;
      const offset = daysSince(source.start_date, sm.date);
      const newDate = addDays(target.start_date, offset);
      const occupied = newMeals.some(m =>
        m.date === newDate && m.meal_type === sm.meal_type && m.recipe_id
      );
      if (occupied) {
        skipped += 1;
        continue;
      }
      newMeals.push({
        date: newDate, meal_type: sm.meal_type, recipe_id: sm.recipe_id,
        rating: null, rated_at: null, note: '',
      });
      copied += 1;
    }
    const updated: Week = { ...target, meals: newMeals };
    const weeks = prev.weeks.some(x => x.id === targetWeekId)
      ? prev.weeks.map(x => x.id === targetWeekId ? updated : x)
      : [...prev.weeks, updated];
    return { ...prev, weeks };
  });
  return { copied, skipped };
}

export async function upsertWeek(w: Week): Promise<void> {
  await mutate(s => {
    const idx = s.weeks.findIndex(x => x.id === w.id);
    const weeks = [...s.weeks];
    if (idx === -1) weeks.push(w);
    else weeks[idx] = w;
    return { ...s, weeks };
  });
}

export async function updateSettings(patch: Partial<Settings>): Promise<void> {
  await mutate(s => ({ ...s, settings: { ...s.settings, ...patch } }));
}

export function ensureWeek(state: AppState, weekId: string): Week {
  return state.weeks.find(w => w.id === weekId) ?? {
    id: weekId, start_date: weekId, meals: [],
    shopping_list: [], shopping_list_generated_at: null, exported_at: null,
  };
}

export function blankShoppingItemFor(name: string): ShoppingItem {
  return {
    id: newId(), name, quantity_hint: '', grocery_section: 'other',
    used_in_recipes: [], safety_modification: null, bulk_candidate: false,
    checked: false, checked_at: null, added_manually: true,
  };
}

import { generateSlug } from '../lib/slug';

export async function regenerateSlugRemote(): Promise<string> {
  const newSlug = generateSlug();
  if (isOffline()) {
    const next = { ...state.value!, family_slug: newSlug, updated_at: new Date().toISOString() };
    state.value = next;
    saveLocal(newSlug, next);
    slug.value = newSlug;
    return newSlug;
  }
  const r = await fetch('/api/regenerate-slug', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ old_slug: slug.value, new_slug: newSlug }),
  });
  if (!r.ok) throw new Error(`regenerate ${r.status}`);
  return newSlug;
}
